import './DataPager.css'
import type { GlobalStateSetter } from '@/utilities/createGlobalState'
import { currentPage, firstPage, incrementRefresh, lastPage, nextPage, previousPage, totalPages, type DataGridState } from './createDataGridState'
import { IconButton } from '../action/IconButton'
import { SelectBox, type SelectBoxOption } from '../scalar/SelectBox'
import { Spinner } from '../graphics/Spinner'
import { formatDecimal } from '@/utilities/formatters'
import { useInterval } from '@/hooks/useInterval'
import type { Dispatch, SetStateAction } from 'react'

//--------------------------------------------------------------------------------
type DataPagerProps<S extends DataGridState<any>> = {
  state: S
  setState: GlobalStateSetter<S> | Dispatch<SetStateAction<S>>
  refreshAfterSeconds?: number
  alignRight?: boolean
}

export function DataPager<S extends DataGridState<any>>({
  state,
  setState,
  refreshAfterSeconds,
  alignRight,
}: DataPagerProps<S>) {

  //--------------------------------------------------------------------------------
  // Automatically refresh periodically
  useInterval(() => {
    if (!refreshAfterSeconds) return
    if (state.loading) return
    if (state.loadingQuietly) return
    if (
      state.lastRefreshedAt &&
      Date.now() - (state.lastRefreshedAt ?? 0) < refreshAfterSeconds * 1000
    ) return
    incrementRefresh(setState, true)
  }, refreshAfterSeconds ? 10 : 0)

  //--------------------------------------------------------------------------------
  // Render

  if (alignRight) return (
    <div className='DataPager'>

      {state.loading && (
        <div className='Loading'>
          <Spinner />
          <span>Loading</span>
        </div>
      )}

      <IconButton
        icon='redo'
        tooltip='Refresh'
        tooltipPlacement='top'
        onClick={() => incrementRefresh(setState)}
      />

      <DataPagerSummary state={state} />

      <SelectBox
        tooltip='Page size'
        tooltipPlacement='top'
        options={PageSizes}
        valueFromString={value => Number(value) as number}
        value={state.take}
        setValue={value => setState(prev => ({
          ...prev,
          take: Number(value),
          skip: 0,
        }))}
      />

      <DataPagerNavigation state={state} setState={setState} />

    </div>
  )

  return (
    <div className='DataPager'>

      <DataPagerNavigation state={state} setState={setState} />

      <DataPagerSummary state={state} />

      <SelectBox
        tooltip='Page size'
        tooltipPlacement='top'
        options={PageSizes}
        valueFromString={value => Number(value) as number}
        value={state.take}
        setValue={value => setState(prev => ({
          ...prev,
          take: Number(value),
          skip: 0,
        }))}
      />

      <IconButton
        icon='redo'
        tooltip='Refresh'
        tooltipPlacement='top'
        onClick={() => incrementRefresh(setState)}
      />

      {state.loading && (
        <div className='Loading'>
          <Spinner />
          <span>Loading</span>
        </div>
      )}

    </div>
  )
}

//--------------------------------------------------------------------------------
function DataPagerSummary<S extends DataGridState<any>>({ state }: { state: S }) {
  return (
    <span className='DataPagerSummary'>
      {state.totalRows === 0
        ? '0 of 0'
        : (
          formatDecimal(state.skip + 1) +
          ' - ' +
          formatDecimal(Math.min(state.skip + state.take, state.totalRows)) +
          ' of ' +
          formatDecimal(state.totalRows)
        )
      }
    </span>
  )
}

//--------------------------------------------------------------------------------
type DataPagerNavigationProps<S extends DataGridState<any>> = {
  state: S
  setState: GlobalStateSetter<S>,
  includeFirst?: boolean
  includeLast?: boolean
}

export function DataPagerNavigation<S extends DataGridState<any>>({
  state,
  setState,
  includeFirst = true,
  includeLast = true,
}: DataPagerNavigationProps<S>) {
  const hasPrev = currentPage(state) > 1
  const hasNext = currentPage(state) < totalPages(state)
  return (
    <div className='DataPagerButtons'>
      {includeFirst && (
        <IconButton
          icon='chevronLeft'
          tooltip='First'
          tooltipPlacement='top'
          disabled={!hasPrev}
          onClick={() => firstPage(setState)}
        />
      )}
      <IconButton
        icon='chevronLeft'
        tooltip='Previous'
        tooltipPlacement='top'
        disabled={!hasPrev}
        onClick={() => previousPage(setState)}
      />
      <IconButton
        icon='chevronRight'
        tooltip='Next'
        tooltipPlacement='top'
        disabled={!hasNext}
        onClick={() => nextPage(setState)}
      />
      {includeLast && (
        <IconButton
          icon='chevronRight'
          tooltip='Last'
          tooltipPlacement='top'
          disabled={!hasNext}
          onClick={() => lastPage(setState)}
        />
      )}
    </div>
  )
}

//--------------------------------------------------------------------------------
export const PageSizes: SelectBoxOption<number>[] = [
  { label: '1', value: 1 },
  { label: '3', value: 3 },
  { label: '5', value: 5 },
  { label: '10', value: 10 },
  { label: '25', value: 25 },
  { label: '50', value: 50 },
  { label: '100', value: 100 },
  { label: '200', value: 200 },
  { label: '500', value: 500 },
  { label: '1,000', value: 1000 },
]
